import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  landing_page?: string;
  first_visit?: string;
  last_visit?: string;
}

const UTM_STORAGE_KEY = 'obrim_utm_params';

export function useUTMTracking() {
  const [searchParams] = useSearchParams();

  // Captura UTM params da URL
  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');
    const utmContent = searchParams.get('utm_content');
    const utmTerm = searchParams.get('utm_term');
    const refCode = searchParams.get('ref');

    // Se tem algum UTM param, salva
    if (utmSource || utmMedium || utmCampaign || refCode) {
      const existingParams = getStoredUTMParams();
      
      const newParams: UTMParams = {
        ...existingParams,
        utm_source: utmSource || existingParams.utm_source,
        utm_medium: utmMedium || existingParams.utm_medium,
        utm_campaign: utmCampaign || existingParams.utm_campaign,
        utm_content: utmContent || existingParams.utm_content,
        utm_term: utmTerm || existingParams.utm_term,
        ref: refCode || existingParams.ref,
        landing_page: window.location.pathname,
        last_visit: new Date().toISOString(),
      };

      // Primeira visita
      if (!existingParams.first_visit) {
        newParams.first_visit = new Date().toISOString();
      }

      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(newParams));
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(newParams));
    }
  }, [searchParams]);

  const getStoredUTMParams = useCallback((): UTMParams => {
    try {
      const stored = localStorage.getItem(UTM_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, []);

  const getUTMParams = useCallback((): UTMParams => {
    // Prioriza session storage (mais recente), depois local storage
    try {
      const session = sessionStorage.getItem(UTM_STORAGE_KEY);
      if (session) return JSON.parse(session);
      
      const local = localStorage.getItem(UTM_STORAGE_KEY);
      if (local) return JSON.parse(local);
    } catch {
      // ignore
    }
    return {};
  }, []);

  const getAttributionData = useCallback(() => {
    const params = getUTMParams();
    
    return {
      source: params.utm_source || 'direct',
      medium: params.utm_medium || 'none',
      campaign: params.utm_campaign || 'none',
      content: params.utm_content,
      term: params.utm_term,
      referralCode: params.ref,
      landingPage: params.landing_page || '/',
      firstVisit: params.first_visit,
      lastVisit: params.last_visit,
    };
  }, [getUTMParams]);

  // Track evento customizado com atribuição
  const trackEvent = useCallback((eventName: string, eventData?: Record<string, any>) => {
    const attribution = getAttributionData();
    
    // Meta Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', eventName, {
        ...eventData,
        utm_source: attribution.source,
        utm_medium: attribution.medium,
        utm_campaign: attribution.campaign,
      });
    }

    // Console log para debug (remover em produção se quiser)
    console.log('[UTM Tracking]', eventName, { ...eventData, attribution });
  }, [getAttributionData]);

  // Track conversão (signup, purchase, etc)
  const trackConversion = useCallback((type: 'Lead' | 'CompleteRegistration' | 'StartTrial' | 'Purchase', value?: number) => {
    const attribution = getAttributionData();
    
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', type, {
        value: value,
        currency: 'BRL',
        utm_source: attribution.source,
        utm_medium: attribution.medium,
        utm_campaign: attribution.campaign,
        content_name: attribution.landingPage,
      });
    }

    console.log('[Conversion Tracked]', type, { value, attribution });
  }, [getAttributionData]);

  return {
    getUTMParams,
    getAttributionData,
    trackEvent,
    trackConversion,
  };
}
